import React from "react";
import {
  Box, Paper, Typography, alpha,
} from "@mui/material";
import BackupIcon from "@mui/icons-material/Backup";
import {
  NAVY, GOLD, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

const BackupPage: React.FC = () => (
  <Box sx={{ p: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: 1.5,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(135deg, ${alpha("#f59e0b", 0.18)} 0%, ${alpha("#f59e0b", 0.06)} 100%)`,
        color: "#f59e0b",
      }}>
        <BackupIcon />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
          Backup Reference
        </Typography>
        <Typography fontSize={12} color={TEXT_SECONDARY}>
          What to back up and where it lives. There is no automated backup
          scheduled — your devops team should wire these into your existing
          backup pipeline.
        </Typography>
      </Box>
    </Box>

    <PathCard
      title="Database"
      paths={[
        "Database connection from .env (DB_DATABASE)",
      ]}
      cmds={[
        "mysqldump -u {DB_USERNAME} -p {DB_DATABASE} > vmd_$(date +%F).sql",
        "# Restore:  mysql -u user -p db < vmd_2026-06-11.sql",
      ]}
      notes={[
        "Backup at LEAST nightly. Includes companies, devices, playlists, media metadata, signage_activities, billing state, and app_releases.",
        "Media bytes are NOT in the DB — they live on disk (see below).",
      ]}
    />

    <PathCard
      title="Uploaded Media (signage content)"
      paths={[
        "storage/app/public/signage/",
      ]}
      cmds={[
        "tar -czf media_$(date +%F).tar.gz -C /var/www/display/backend/storage/app/public signage",
      ]}
      notes={[
        "These are the original jpg/png/webp/mp4 files uploaded by tenants.",
        "Size grows with tenant count — plan for gigabytes per active customer.",
        "Files are referenced by checksum in the player cache; losing this directory means every player re-downloads on next sync.",
      ]}
    />

    <PathCard
      title="Player APKs (release pipeline)"
      paths={[
        "storage/app/public/releases/android/",
      ]}
      cmds={[
        "tar -czf releases_$(date +%F).tar.gz -C /var/www/display/backend/storage/app/public releases",
      ]}
      notes={[
        "Each row in the app_releases table points at a file here.",
        "If you lose this directory, the file_size + checksum on the DB rows still match what devices have already downloaded — but new devices won't be able to fetch.",
      ]}
    />

    <PathCard
      title="Device Screenshots (latest only)"
      paths={[
        "storage/app/public/screenshots/{device_code}/latest.jpg",
      ]}
      cmds={[
        "# Optional — these are overwritten on every screenshot upload anyway",
        "tar -czf screenshots_$(date +%F).tar.gz -C /var/www/display/backend/storage/app/public screenshots",
      ]}
      notes={[
        "Screenshots overwrite the previous file — no history is kept by design.",
        "Backing up is only useful if you want a forensic snapshot of what was on every screen at a given moment.",
      ]}
    />

    <PathCard
      title="Application Code + .env"
      paths={[
        "/var/www/display/backend/.env",
        "/var/www/display/frontend/.env.production",
      ]}
      cmds={[
        "# Already in git — back up only the .env files which contain secrets",
        "cp /var/www/display/backend/.env vmd_env_$(date +%F).backup",
      ]}
      notes={[
        ".env holds DB password, APP_KEY, mail credentials. Losing APP_KEY rotates all encrypted cookies and Sanctum tokens.",
        "DO NOT commit .env to git. Keep backups in your secrets manager (1Password / AWS Secrets Manager / Hashicorp Vault).",
      ]}
    />

    <Paper elevation={0} sx={{
      mt: 4, p: 3, borderRadius: 3,
      background: `linear-gradient(135deg, ${alpha(GOLD, 0.06)} 0%, ${alpha(GOLD, 0.02)} 100%)`,
      border: `1px solid ${alpha(GOLD, 0.3)}`,
    }}>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, mb: 1 }}>
        Restore order
      </Typography>
      <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY }}>
        1. Restore <code>.env</code> first so APP_KEY matches the encrypted columns.<br />
        2. Restore the database dump.<br />
        3. Restore the storage/ directories.<br />
        4. Run <code>php artisan storage:link</code>.<br />
        5. Run <code>php artisan migrate</code> (no-op if dump is fresh).<br />
        6. Restart the queue worker + scheduler cron.<br />
      </Typography>
    </Paper>
  </Box>
);

const PathCard: React.FC<{
  title: string; paths: string[]; cmds: string[]; notes: string[];
}> = ({ title, paths, cmds, notes }) => (
  <Paper elevation={0} sx={{
    mb: 3, borderRadius: 3, border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: "#fff",
  }}>
    <Box sx={{
      bgcolor: alpha("#0e7c9b", 0.04),
      borderBottom: `1px solid ${alpha("#0e7c9b", 0.18)}`,
      px: 3, py: 1.5,
    }}>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
        textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
        Paths
      </Typography>
      {paths.map((p) => (
        <Box key={p} sx={{ fontFamily: "monospace", fontSize: 12, color: NAVY,
          bgcolor: "#f1f5f9", p: 1, borderRadius: 1, mb: 0.5 }}>
          {p}
        </Box>
      ))}
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
        textTransform: "uppercase", letterSpacing: "0.06em", mt: 2, mb: 1 }}>
        Suggested commands
      </Typography>
      <Box component="pre" sx={{
        m: 0, p: 2, borderRadius: 1, bgcolor: "#0b1e2d", color: "#cbd5e1",
        fontSize: 11, fontFamily: "monospace", whiteSpace: "pre-wrap",
      }}>
        {cmds.join("\n")}
      </Box>
      {notes.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {notes.map((n, i) => (
            <Typography key={i} sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 0.5 }}>
              • {n}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  </Paper>
);

export default BackupPage;
